import { UserDisplayName, UserOptionLabel, ViperSelect } from "@/components";
import { ReadOnlyUserInput } from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import { useGetUsersByIdsMutation, useSuggestUsersQuery } from "@/services";
import { Input, Select, Spin } from "antd";
import { arrayOf, bool, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const { Option } = Select;

export const UserSelect = ({
  users: previouslySelectedUserIds = [],
  limitedOptions: limitedListUserIds,
  readOnly,
  multipleSelect = true,
  dataTestId,
  showDeletedUsers = false,
  ...rest
}) => {
  const intl = useIntl();

  const [query, setQuery] = useState();
  const [selectableUsers, setSelectableUsers] = useState([]);

  const {
    data: suggestedUsersByQuery,
    isUninitialized: isSuggestUsersUninitialized,
    isLoading: isSuggestUsersLoading,
  } = useSuggestUsersQuery(
    { query, additionalParameters: { showDeleted: showDeletedUsers } },
    { skip: !query }
  );
  const [getUsersByIds, { data: users, isLoading: isLoadingUsersByIds }] =
    useGetUsersByIdsMutation();

  useEffect(() => {
    if (isSuggestUsersUninitialized || isSuggestUsersLoading) return;

    setSelectableUsers(suggestedUsersByQuery?.map(({ document }) => document));
  }, [
    isSuggestUsersLoading,
    isSuggestUsersUninitialized,
    suggestedUsersByQuery,
  ]);

  useEffect(() => {
    if (query) return;

    setSelectableUsers(users);
  }, [query, users]);

  const selectedUserIdsKey = [
    ...new Set([
      ...(limitedListUserIds || []),
      ...(previouslySelectedUserIds || []),
    ]),
  ].join(",");

  useEffect(() => {
    const uniqueUserIds = selectedUserIdsKey
      ? selectedUserIdsKey.split(",")
      : [];

    if (!uniqueUserIds.length) return;

    getUsersByIds(uniqueUserIds);
  }, [getUsersByIds, selectedUserIdsKey]);

  if (readOnly) {
    if (!previouslySelectedUserIds.length)
      return <Input disabled className="border-none" />;

    if (isLoadingUsersByIds && !users) {
      return (
        <Input
          disabled
          className="border-none"
          suffix={<Spin size="small" />}
          data-test-id={dataTestId && `${dataTestId}-user-select-read-only-loading`}
        />
      );
    }

    return users
      ?.filter(({ id }) => previouslySelectedUserIds.includes(id))
      .map((user) => (
        <ReadOnlyUserInput
          key={user.id}
          loading={isLoadingUsersByIds}
          user={user}
          dataTestId={dataTestId}
        />
      ));
  }

  if (limitedListUserIds) {
    return (
      <S.SelectArrowStyle
        mode="multiple"
        placeholder={intl.formatMessage({ id: "general.select" })}
        showSearch={false}
        loading={isLoadingUsersByIds}
        optionLabelProp="label"
        data-test-id={dataTestId && `${dataTestId}-user-select-limited-options`}
        {...rest}
      >
        {selectableUsers?.map(
          ({
            id,
            displayName,
            department,
            email,
            jobTitle,
            azureAdAccountEnabled,
          }) => {
            const userNoLongerInLimitedList = !limitedListUserIds.includes(id);

            return (
              <Option
                key={id}
                label={
                  <UserDisplayName
                    displayName={displayName}
                    userAccountEnabled={azureAdAccountEnabled}
                  />
                }
                className={userNoLongerInLimitedList && "hidden"}
                aria-hidden={userNoLongerInLimitedList}
              >
                <UserOptionLabel
                  displayName={displayName}
                  department={department}
                  email={email}
                  jobTitle={jobTitle}
                />
              </Option>
            );
          }
        )}
      </S.SelectArrowStyle>
    );
  }

  return (
    <ViperSelect
      dataTestId={dataTestId && `${dataTestId}-user-select`}
      customOptions={selectableUsers?.map(
        ({
          id,
          displayName,
          department,
          email,
          jobTitle,
          azureAdAccountEnabled,
        }) => (
          <Option
            key={id}
            label={
              <UserDisplayName
                displayName={displayName}
                userAccountEnabled={azureAdAccountEnabled}
              />
            }
          >
            <UserOptionLabel
              displayName={displayName}
              department={department}
              email={email}
              jobTitle={jobTitle}
            />
          </Option>
        )
      )}
      allowMultipleSelections={multipleSelect}
      onFetchSuggestions={setQuery}
      placeholder={intl.formatMessage({ id: "general.search" })}
      loading={isLoadingUsersByIds}
      optionLabelProp="label"
      {...rest}
    />
  );
};

UserSelect.propTypes = {
  users: arrayOf(string),
  limitedOptions: arrayOf(string),
  readOnly: bool,
  disabled: bool,
  multipleSelect: bool,
  dataTestId: string,
  showDeletedUsers: bool,
};
